import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import WPLoader from "../../packages/WPLoader";
import { useToastNotification } from "../../packages/WPToastNotification";

import Header from "../../components/Header";
import PageNavigation from "../../components/PageNavigation";
import { CLEANING_ADVISOR_NAVIGATION_PAGES } from "../../components/PageNavigation/PageNavigation.config";
import XLSFileUpload from "../../FileUpload";
import { Language_Currency_Mapping } from "./Settings/settingsOptions.config";

import Footer from "../../common/footer/Footer";

import {
  extractPlantId,
  getDatasetDetails,
  getSelectedPlantTrainInfo,
} from "../../utils/Utils";

import { API_URLS } from "../../network/appUrls";
import { getLastFileDetails } from "../../network/cipAPI";

import {
  storeDefaultInformation,
  storeFileId,
  storePlantDetails,
  storeTrainChemicalSelection,
  storeTrainUtilityCosts,
  storeUnits,
  updateSelectedTrain,
} from "../../Store/TemplateStore/template.action";

import "./index.scss";

import "../../styles/vendors/style";

const RoAdvisorHome = (props) => {
  const { t, i18n } = useTranslation();
  const { plantData, token } = useSelector((state) => state);
  const { showErrorNotification } = useToastNotification();

  const [activeStep, setActiveStep] = useState(0);
  const [isBackClicked, setIsBackClicked] = useState(false);
  const [isFirstTimeLoad, setIsFirstTimeLoad] = useState(false);
  const [fileDetailsLoaded, setFileDetailsLoaded] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);
  const [advancedSettingsConfig, setAdvancedSettingsConfig] = useState(null);

  const xlmRef = useRef(null);
  const hasLoaded = useRef(false);

  const handleNext = () => {
    setIsBackClicked(false);
    if (xlmRef && xlmRef.current && (activeStep === 0 || activeStep === 1)) {
      xlmRef.current.onNextClicked();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setIsBackClicked(true);
    if (activeStep - 1 >= 0) setActiveStep(activeStep - 1);
  };

  const handleRibbonPrevious = () => {
    if (activeStep === 0) {
      navigate(`${basePath}home?plantId=${plantId}`);
      return;
    }

    handleBack();
  };

  const isNextDisabled =
    (activeStep === 2 || activeStep === 4) && disableNextButton;

  const handlePageChange = (page, index) => {
    if (index > activeStep) {
      if (!isNextDisabled) handleNext();
    } else {
      setIsBackClicked(true);
      setActiveStep(index);
    }
  };

  const basePath = API_URLS.basePath;

  const plantId = extractPlantId();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (token && plantData.length > 0 && !hasLoaded.current) {
      hasLoaded.current = true;
      getLastFileDetail();
    }
  }, [token, plantData]);

  useEffect(() => {
    if (activeStep === 5) {
      navigate(`${basePath}lifetimeAdvisorHome?plantId=${plantId}`);
    }
  }, [activeStep]);

  const getLastFileDetail = async () => {
    try {
      const trainToUse = getSelectedPlantTrainInfo(plantData, plantId);
      const fileDetails = await getLastFileDetails(trainToUse);
      if (fileDetails && fileDetails?.data) {
        setIsFirstTimeLoad(true);
        const data = fileDetails.data;
        const {
          owner_name,
          city,
          country,
          name,
          water_type,
          num_trains,
          tol_num_elements,
          sel_pass,
          sel_stage,
          sel_pressure_vessels,
          sel_element_involved,
          file,
          sel_element_type,
          is_dupont_element,
          utility_costs,
          default_cip: defaultInfo,
          setting: settings,
          chemical_selection,
          sel_train,
        } = data;
        const plantInformation = {
          plantLocation: `${city ? ` ${city}, ${country}` : `${country}`}`,
          plantName: name,
          water_type,
          num_trains,
          tol_num_elements,
          sel_train,
          sel_pass,
          sel_stage,
          sel_pressure_vessels,
          sel_element_involved,
          sel_element_type,
          is_dupont_element: is_dupont_element ? "No" : "Yes",
        };
        dispatch(storePlantDetails(plantInformation));
        dispatch(updateSelectedTrain(sel_train));
        dispatch(
          storePlantDetails({
            dataset_name: getDatasetDetails({
              owner_username: owner_name,
              min_date: file?.min_date,
              max_date: file?.max_date,
            }),
          }),
        );

        dispatch(storeTrainChemicalSelection(chemical_selection));

        dispatch(storeTrainUtilityCosts(utility_costs));

        if (defaultInfo) {
          dispatch(storeDefaultInformation(defaultInfo));
        }

        if (file?.file_id) {
          dispatch(
            storeFileId({
              file_id: file.file_id,
            }),
          );
        }

        const unitInformation = {
          temperature: settings?.temperature,
          permeate_quality: settings?.permeate_quality,
          feed_quality: settings?.feed_quality,
          pressure: settings?.pressure,
          flow: settings?.flow,
          volume: settings?.volume,
          mass: settings?.mass,
          currency:
            settings?.currency ||
            parseFloat(Language_Currency_Mapping(i18n.language).currency),
          conversion_rate:
            settings?.conversion_rate ||
            parseFloat(
              Language_Currency_Mapping(i18n.language).conversion_rate,
            ),
          feed_water_permeate_factor: settings?.feed_water_permeate_factor,
          feed_water_factor: settings?.feed_water_factor,
        };
        dispatch(storeUnits(unitInformation));
        setTimeout(() => {
          setIsFirstTimeLoad(false);
        }, 1000);
      }
    } catch (err) {
      showErrorNotification(t("notificationStrings.somethingWrong"));
    } finally {
      setFileDetailsLoaded(true);
    }
  };

  return (
    <>
      {activeStep !== 5 && (
        <div className="container-main">
          <Header
            showPlantMenu
            hideCosts
            activeStep={activeStep}
            advancedSettingsConfig={advancedSettingsConfig}
            navigationConfig={{
              activePageKey: CLEANING_ADVISOR_NAVIGATION_PAGES[activeStep]?.key,
              isNextDisabled,
              onPreviousClick: handleRibbonPrevious,
              onNextClick: handleNext,
            }}
          />
          <PageNavigation
            activePageKey={CLEANING_ADVISOR_NAVIGATION_PAGES[activeStep]?.key}
            onPageChange={handlePageChange}
          />
          <div className="main-container">
            <div className="content-container">
              {fileDetailsLoaded ? (
                <div>
                  <XLSFileUpload
                    ref={xlmRef}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    isFirstTimeLoad={isFirstTimeLoad}
                    isBackClicked={isBackClicked}
                    setIsBackClicked={setIsBackClicked}
                    setDisableNextButton={setDisableNextButton}
                    setAdvancedSettingsConfig={setAdvancedSettingsConfig}
                  />
                </div>
              ) : (
                <WPLoader />
              )}
            </div>
            <Footer />
          </div>
        </div>
      )}
    </>
  );
};

export default RoAdvisorHome;
